import { z } from "zod"

// ─── Status / Mode enums ─────────────────────────────────────────────────────

export type CourseStatus = "draft" | "active" | "archived"
export type CourseMode = "free" | "rag" | "hybrid"
export type DocumentStatus = "pending" | "processing" | "ready" | "failed"

// ─── Domain interfaces ────────────────────────────────────────────────────────

export interface Course {
  id: string
  userId: string
  title: string
  subject: string | null
  description: string | null
  mode: CourseMode
  language: string
  status: CourseStatus
  settings: Record<string, unknown>
  documentsCount: number
  sessionCount: number
  personaId: string | null
  createdAt: string
  updatedAt: string
}

export interface CourseDocument {
  id: string
  courseId: string
  originalName: string
  storagePath: string
  mimeType: string
  size: number
  status: DocumentStatus
  chunksCount: number
  createdAt: string
}

// ─── Form schemas ─────────────────────────────────────────────────────────────

export const createCourseSchema = z.object({
  title: z
    .string()
    .min(1, "courses.errors.title_required")
    .max(100, "courses.errors.title_max"),
  subject: z
    .string()
    .max(50, "courses.errors.subject_max")
    .nullable()
    .optional()
    .or(z.literal("")),
  description: z
    .string()
    .max(1000, "courses.errors.description_max")
    .optional()
    .or(z.literal("")),
  mode: z.enum(["free", "rag", "hybrid"]).default("free"),
  language: z.enum(["fr", "en"]).default("fr"),
  persona_id: z.uuid().nullable().optional(),
})

export type CreateCourseFormData = z.infer<typeof createCourseSchema>

/** Valeurs telles que saisies : `mode` et `language` ont des valeurs par défaut. */
export type CreateCourseFormInput = z.input<typeof createCourseSchema>

export const updateCourseSchema = z.object({
  title: z.string().min(1, "courses.errors.title_required").max(100).optional(),
  subject: z.string().min(1, "courses.errors.subject_required").max(50).nullable().optional(),
  description: z.string().max(1000).optional().or(z.literal("")),
  mode: z.enum(["free", "rag", "hybrid"]).optional(),
  language: z.enum(["fr", "en"]).optional(),
  persona_id: z.uuid().nullable().optional(),
})

export type UpdateCourseFormData = z.infer<typeof updateCourseSchema>

// ─── Query filters ────────────────────────────────────────────────────────────

export interface CourseFilters {
  status?: CourseStatus
  subject?: string
  search?: string
  mode?: CourseMode
  page?: number
  limit?: number
}
